import _cloneDeep from 'lodash.clonedeep'
import _debounce from 'lodash.debounce'
import { DeepDiff } from 'deep-diff'
import QuoteService from '../QuoteService'
import QuoteAutoSaveService from '../QuoteAutoSaveService'
import { CustomerService, VehicleService, InsurerService } from '@/services'
import QuoteLiteModel from '@/classes/viewmodels/Quote/QuoteLiteModel'
import QuoteRoutes from '../route-types'
import Guid from '@/components/Guid'
import StoreUtil from '@/store/utils'
import { EventHubTypes } from '@/enums'

export function createAutoSaveHelper(vm) {

  const quoteItemCollectionKeys = ['labours', 'parts', 'miscs', 'others', 'sublets']

  const setAutoSaveState = (state = {}) => {
    const { isEditing, isSaving, isFailed, failMessage } = state
    if (typeof isEditing === 'boolean') {
      vm.autoSaveStatus.isEditing = isEditing
      vm.autoSaveStatus.isDiffChanged = isEditing
    }
    if (typeof isSaving === 'boolean') {
      vm.autoSaveStatus.isSaving = isSaving
    }
    if (typeof isFailed === 'boolean') {
      vm.autoSaveStatus.isFailed = isFailed
      if (!isFailed && typeof failMessage === 'undefined') {
        vm.autoSaveStatus.failMessage = ''
      }
    }
    if (typeof failMessage === 'string') {
      vm.autoSaveStatus.failMessage = failMessage
    }
  }

  const isAnyEntityNew = () => {
    return (
      (vm.entity && vm.entity.isNew) ||
      (vm.customer && vm.customer.isNew) ||
      (vm.vehicle && vm.vehicle.isNew) ||
      (vm.insurer && vm.insurer.isNew)
    )
  }

  const registerAutoSaveFailure = (failMessage) => {
    vm.autoSaveFailureCount += 1
    const shouldLock = !vm.autoSaveDisabled && vm.autoSaveFailureCount >= vm.autoSaveFailureLimit
    if (shouldLock) {
      vm.autoSaveDisabled = true
      if (vm.autoSaveQuote && typeof vm.autoSaveQuote.cancel === 'function') {
        vm.autoSaveQuote.cancel()
      }
      if (vm.autoUpdateQuoteHeader && typeof vm.autoUpdateQuoteHeader.cancel === 'function') {
        vm.autoUpdateQuoteHeader.cancel()
      }

      vm.$notification.openNotificationWithType(
        'danger',
        'Autosave Paused',
        'Autosave has been paused due to repeated failures. Please check your connection and try saving manually.'
      )
    }
  }

  const resetAutoSaveFailures = () => {
    vm.autoSaveFailureCount = 0
    if (vm.autoSaveDisabled && vm.autoSaveEnabledToggle) {
      vm.autoSaveDisabled = false
    }
  }

  const hasPendingAutoSaveDiffs = () => {
    return Boolean(
      vm.snapshotDiff ||
      vm.snapshotDiffCustomer ||
      vm.snapshotDiffVehicle ||
      vm.snapshotDiffInsurer
    )
  }

  const hasPendingAutoSaveWork = () => {
    return (
      vm.autoSaveStatus.isSaving ||
      vm.autoSaveStatus.isEditing ||
      vm.headerAutosaveInFlight ||
      hasPendingItemsSave()
    )
  }

  const getExpandedQuoteItemId = () => {
    const expandedQuoteItem = document.querySelector('.quote-builder-item-row.is-selected')
    return expandedQuoteItem ? expandedQuoteItem.id : null
  }

  const isAnyQuoteItemDropdownOpen = () => {
    // Scope queries to the quote UI to avoid blocking unrelated dropdowns elsewhere in the app.
    // QuoteView renders quote content inside '.quote-body'.
    const scope = document.querySelector('.quote-body') || document

    // Item description autocomplete (BulmaAutocomplete)
    try {
      const dropdownMenus = scope.querySelectorAll('.autocomplete .dropdown-menu')
      for (const el of dropdownMenus) {
        if (!el) continue
        const style = window.getComputedStyle(el)
        if (style && style.display !== 'none' && style.visibility !== 'hidden') {
          return true
        }
      }
    } catch (e) {
      // no-op
    }

    // Sublet vendor dropdown (vue-multiselect)
    if (scope.querySelector('.multiselect--active')) {
      return true
    }

    // Sublet type is a native <select>; we can't reliably detect "open", so treat focused as "open".
    const activeElement = document.activeElement
    if (activeElement && activeElement.tagName === 'SELECT') {
      // Only treat selects within the quote UI as blocking.
      if (!activeElement.closest || !activeElement.closest('.quote-body')) return false
      const id = activeElement.getAttribute('id') || ''
      if (id.endsWith('-sublet-type')) {
        return true
      }
    }

    return false
  }

  // True when the user is actively editing an input control inside a quote ITEM ROW
  // (textbox, textarea, native select, or contenteditable). Saving while a row control
  // is focused must be avoided: the post-save entity refresh re-renders the item rows,
  // which steals focus and closes any open dropdown mid-interaction. Scoped to the item
  // rows so editing in the butterfly/add panel or header fields doesn't block item saves.
  const isEditableElementFocused = () => {
    const el = document.activeElement
    if (!el || el === document.body) return false
    if (typeof el.closest !== 'function' || !el.closest('.quote-builder-item-row')) return false
    const tag = el.tagName
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
    if (el.isContentEditable) return true
    return false
  }

  // The user is "busy" — and a save must be deferred — while either an editable
  // control is focused or a custom dropdown (autocomplete / multiselect) is open.
  const isQuoteItemEditInProgress = () => {
    return isEditableElementFocused() || isAnyQuoteItemDropdownOpen()
  }

  const getFocusedItemInfo = () => {
    const activeElement = document.activeElement
    if (!activeElement || typeof activeElement.closest !== 'function') return null

    // Determine which quote item row we're in.
    const rowEl = activeElement.closest('.quote-builder-item-row')
    const rowId = rowEl && rowEl.id ? rowEl.id : null
    if (!rowId) return null

    // Find the item across all item arrays.
    const allItems = quoteItemCollectionKeys.flatMap((key) => vm.entity?.[key] || [])
    const item = allItems.find((i) => i && i.quoteItemId === rowId)
    if (!item) return null

    // Capture the id suffix of the focused field within the row.
    // Most quote-builder inputs use ids like `${quoteItemId}-itemDesc`, `${quoteItemId}-unit`, etc.
    // If the active element itself doesn't have the id, walk up the DOM within the row.
    let fieldElement = null
    if (activeElement.id && activeElement.id.startsWith(rowId)) {
      fieldElement = activeElement
    } else {
      let cursor = activeElement
      while (cursor && cursor !== rowEl) {
        if (cursor.id && cursor.id.startsWith(rowId)) {
          fieldElement = cursor
          break
        }
        cursor = cursor.parentElement
      }
    }

    const fieldSuffix = fieldElement && fieldElement.id
      ? fieldElement.id.substring(rowId.length)
      : '-itemDesc'

    return {
      lineNumber: item.lineNumber,
      itemType: item.itemType,
      itemDesc: item.itemDesc,
      // IMPORTANT: this suffix is concatenated by QuoteBuilderItemsView as `${newItemId}${fieldSuffix}`.
      // Keep the leading '-' so ids resolve correctly.
      fieldSuffix
    }
  }

  const findItemIdByLineAndType = (lineNumber, itemType) => {
    const allItems = quoteItemCollectionKeys.flatMap((key) => vm.entity?.[key] || [])
    const item = allItems.find(i => i.lineNumber === lineNumber && i.itemType === itemType)
    return item ? item.quoteItemId : null
  }

  // Blank = null/undefined or whitespace-only. Blank-description items are never
  // autosaved; new ones are purged on tab leave and synced ones revert to last-saved.
  const isBlankItemDesc = (desc) => desc === null || desc === undefined || String(desc).trim() === ''

  const isValidItemAutoSave = (item) => {
    return !!item && !isBlankItemDesc(item.itemDesc)
  }

  const autoSaveQuoteAssets = async () => {

    const autoSaveEnabled = vm.autoSaveEnabled && !vm.entity.isNew && !vm.entity.readOnly

    let promises = []
    try {
      if (vm.snapshotDiffCustomer) {
        // promises.splice(promises.length, 1, CustomerService.putCustomer(vm.customer, vm.snapshotDiffCustomer))
        await CustomerService.putCustomer(vm.customer, vm.snapshotDiffCustomer, autoSaveEnabled ? true : false)
      }

      if (vm.snapshotDiffVehicle) {
        vm.vehicle.excludeQuoteId = vm.entity.quoteId
        promises.splice(promises.length, 1, VehicleService.putEntity(vm.vehicle, vm.snapshotDiffVehicle, autoSaveEnabled ? true : false))
      }

      if (vm.snapshotDiffInsurer) {
        promises.splice(promises.length, 1, InsurerService.putInsurer(vm.insurer, vm.snapshotDiffInsurer, autoSaveEnabled ? true : false))
      }

      if (promises.length > 0) {
        await Promise.all(promises)
      }
      return true
    }
    catch (e) {
      vm.customerSaveAction = ''
      const msg = e.response?.data?.message || e.message
      vm.$notification.openNotificationWithType('danger', 'Quote Assets', msg)
      return false
    }
  }

  const hasHeaderQuoteIdChanges = () => {
    if (!vm.entity || !vm.baseSnapshot) return false
    const fields = ['vehicleId', 'customerId', 'insurerId']
    return fields.some((field) => vm.baseSnapshot?.[field] !== vm.entity?.[field])
  }

  const saveQuoteAfterHeaderChangeNow = async () => {
    if (!vm.entity || !vm.baseSnapshot) return true
    if (vm.entity.readOnly || isAnyEntityNew()) return true

    // Don't autosave an invalid quote.
    if (vm.validateError) {
      setAutoSaveState({
        isFailed: true,
        failMessage: 'Quote couldn\'t be saved due to validation errors.'
      })
      return false
    }

    // Save the quote itself (including any changes that the header selection caused,
    // e.g. insurer selection updating markups/rates/template items).
    const diff = DeepDiff.diff(vm.baseSnapshot, vm.entity, (path, key) => path.length === 0 && key === 'modifiedDate')
    if (!diff) return true

    const isCustomerInfoChanged = await vm.triggerIsNewOwnerPrompt()
    if (!vm.vehicle?.isNew && !vm.customer?.isNew && isCustomerInfoChanged) {
      vm.isCustomerCreateModifyModalActive = true
      return false
    }

    await vm.assignLineNumbers(false)
    await QuoteService.putEntity(_cloneDeep(vm.entity), diff)

    // Mark saved locally by updating the base snapshot.
    vm.setBaseSnapshot(_cloneDeep(vm.entity))
    vm.saveSnapshot(_cloneDeep(vm.entity))
    return true
  }

  const autoSave = async () => {
      try {
        if (vm.validateError) {
          console.error('Validation errors', vm.$v)
          return {
            status: 'failed',
            failMessage: 'Quote couldn\'t be saved due to validation errors.'
          }
        }

        vm.saveSnapshot(_cloneDeep(vm.entity))
        const isCustomerInfoChanged = await vm.triggerIsNewOwnerPrompt()
        if (!vm.vehicle.isNew && !vm.customer.isNew && isCustomerInfoChanged) {
          return { status: 'blocked' }
        }
        await vm.assignLineNumbers(false)

        if (!vm.snapshotDiff) {
          return { status: 'no-diff' }
        }
        await QuoteService.putEntity(_cloneDeep(vm.entity), vm.snapshotDiff)

        await vm.getEntity(true, true)

        // Update attachments count if any (copied directly from original)
        // Note: Logic in original seemed specific, ensuring we keep it
        return { status: 'saved' }
      } catch (error) {
        console.error('Quote auto save failed', error)
        if (error?.response?.status === 409) {
          return { status: 'conflict' }
        }
        return {
          status: 'error',
          failMessage: error?.message || 'Automatic quote save failed.'
        }
      }
  }

  // Define autosave functions
  const autoSaveQuote = async function () {
    if (!vm.autoSaveEnabled || isAnyEntityNew()) {
      return
    }

    // Hard guard: if any quote-item dropdown is open, do not save.
    // This catches cases where autosave was scheduled before the dropdown opened.
    if (isAnyQuoteItemDropdownOpen()) {
      setAutoSaveState({ isEditing: true, isSaving: false })
      // Defer the header/entity autosave until any open quote-item dropdown closes.
      startQuoteAutosaveDropdownDeferredMonitor('quote:autoSaveQuote')
      return
    }
    setAutoSaveState({ isSaving: true, isFailed: false })
    try {
      const result = await autoSave()
      switch (result?.status) {
        case 'saved': {
          resetAutoSaveFailures()
          const pending = hasPendingAutoSaveDiffs()
          setAutoSaveState({
            isSaving: pending,
            isEditing: pending,
            isFailed: false
          })
          break
        }
        case 'no-diff':
          resetAutoSaveFailures()
          setAutoSaveState({
            isSaving: false,
            isEditing: false,
            isFailed: false
          })
          break
        case 'blocked':
          setAutoSaveState({ isSaving: false, isEditing: true })
          break
        case 'conflict':
          setAutoSaveState({ isSaving: false, isEditing: true })
          vm.isSaveConflictModalActive = true
          break
        case 'failed':
        case 'error':
          setAutoSaveState({
            isSaving: false,
            isEditing: true,
            isFailed: true,
            failMessage: result.failMessage || 'Automatic quote save failed.'
          })
          registerAutoSaveFailure(result?.failMessage)
          break
        default:
          setAutoSaveState({ isSaving: false })
      }
    } catch (error) {
      console.error('Quote auto save failed', error)
      setAutoSaveState({
        isSaving: false,
        isFailed: true,
        failMessage: error?.message || 'Automatic quote save failed.'
      })
    } finally {
      vm.headerSavingPanels = { vehicle: false, customer: false, insurer: false, booking: false }
    }
  }

  const autoUpdateQuoteHeader = async function (requestToken) {
    if (!vm.autoSaveEnabled || isAnyEntityNew()) {
      return
    }
    const toComparable = (value) => {
      const cloned = _cloneDeep(value)
      if (cloned && typeof cloned === 'object') {
        delete cloned.modifiedDate
      }
      return cloned
    }

    const customerBaseline = vm.headerAutosaveBaseline.customer || vm.quoteCustomer || vm.currentCustomerSnapshot
    const vehicleBaseline = vm.headerAutosaveBaseline.vehicle || vm.quoteVehicle || vm.currentVehicleSnapshot
    const insurerBaseline = vm.headerAutosaveBaseline.insurer || vm.quoteInsurer || vm.currentInsurerSnapshot

    const customerDiff = DeepDiff.diff(toComparable(customerBaseline), toComparable(vm.customer))
    const vehicleDiff = DeepDiff.diff(toComparable(vehicleBaseline), toComparable(vm.vehicle))
    const insurerDiff = DeepDiff.diff(toComparable(insurerBaseline), toComparable(vm.insurer))

    if(!customerDiff && !vehicleDiff && !insurerDiff) {
      if (!hasPendingAutoSaveDiffs()) {
        setAutoSaveState({ isEditing: false, isSaving: false })
      }
      if (requestToken === vm.headerAutosaveToken) {
        vm.headerAutosaveInFlight = false
      }
      return
    }

    vm.headerSavingPanels = {
      vehicle: !!vehicleDiff,
      customer: !!customerDiff,
      insurer: !!insurerDiff,
      booking: false
    }

    const isCustomerInfoChanged = await vm.triggerIsNewOwnerPrompt()
    if (!vm.vehicle.isNew && !vm.customer.isNew && isCustomerInfoChanged) {
      vm.isCustomerCreateModifyModalActive = true
      vm.headerSavingPanels = { vehicle: false, customer: false, insurer: false, booking: false }
      setAutoSaveState({ isEditing: true, isSaving: false })
      if (requestToken === vm.headerAutosaveToken) {
        vm.headerAutosaveInFlight = false
      }
      return
    }

    setAutoSaveState({ isEditing: true, isSaving: true, isFailed: false })
    const ok = await autoSaveQuoteAssets(false)
    if (!ok) {
      vm.headerSavingPanels = { vehicle: false, customer: false, insurer: false, booking: false }
      setAutoSaveState({ isSaving: false, isFailed: true })
      // Keep isDiffChanged true so UI reflects unsaved header edits.
      registerAutoSaveFailure('Automatic header save failed.')
      if (requestToken === vm.headerAutosaveToken) {
        vm.headerAutosaveInFlight = false
      }
      return
    }

    // Refresh header assets from server, but don't clobber newer edits.
    vm.suppressHeaderAutosave = true
    // Save pre-refresh state in case we need to revert
    const preRefreshVehicle = _cloneDeep(vm.vehicle)
    const preRefreshCustomer = _cloneDeep(vm.customer)
    const preRefreshInsurer = _cloneDeep(vm.insurer)

    try {
      const refreshPromises = []
      if (Guid.validGuid(vm.entity.vehicleId)) {
        refreshPromises.push(vm.getQuoteVehicle({ id: vm.entity.vehicleId, refresh: true }))
      }
      if (Guid.validGuid(vm.entity.customerId)) {
        refreshPromises.push(vm.getQuoteCustomer({ id: vm.entity.customerId, refresh: true }))
      }
      if (Guid.validGuid(vm.entity.insurerId)) {
        refreshPromises.push(vm.getQuoteInsurer({ id: vm.entity.insurerId, refresh: true }))
      } else {
        refreshPromises.push(vm.getNewInsurer())
      }

      // Fire refresh in background without blocking UI. Mark as saved immediately.
      Promise.all(refreshPromises)
        .then(async () => {
          // Refresh completed - update local state if no newer edits
          // If user changed something while we were refreshing, don't overwrite local edits.
          if (requestToken !== vm.headerAutosaveToken) {
            // Newer request in flight, don't update anything
            return
          }

          // Detect concurrent user edits while refresh was in progress
          const vehicleChanged = !!DeepDiff.diff(preRefreshVehicle, vm.vehicle)
          const customerChanged = !!DeepDiff.diff(preRefreshCustomer, vm.customer)
          const insurerChanged = !!DeepDiff.diff(preRefreshInsurer, vm.insurer)
          const hasConcurrentHeaderEdits = vehicleChanged || customerChanged || insurerChanged

          if (hasConcurrentHeaderEdits) {
            // User made new edits while we were refreshing - don't change saved status
            // Next cycle will handle the new edits
            return
          }

          // No concurrent edits - safe to update from server
          vm.vehicle = _cloneDeep(vm.currentVehicleSnapshot)
          if (vm.vehicle) vm.vehicle.isNew = false
          vm.customer = _cloneDeep(vm.currentCustomerSnapshot)
          vm.insurer = _cloneDeep(vm.currentInsurerSnapshot)

          if (vm.vehicle && vm.customer) {
            vm.vehicle.customer = {
              key: vm.customer.id,
              value: vm.customer.fullname
            }
          }

          vm.headerAutosaveBaseline.vehicle = _cloneDeep(vm.vehicle)
          vm.headerAutosaveBaseline.customer = _cloneDeep(vm.customer)
          vm.headerAutosaveBaseline.insurer = _cloneDeep(vm.insurer)

          // Check if we need to save quote due to header ID changes
          // Header changes (e.g., insurer) may trigger template/markup changes that affect the quote
          if (hasHeaderQuoteIdChanges()) {
            vm.saveSnapshot(_cloneDeep(vm.entity))
            await saveQuoteAfterHeaderChangeNow()
          }
        })
        .catch(err => {
          console.error('Header refresh failed:', err)
          setAutoSaveState({ isFailed: true })
          registerAutoSaveFailure('Header refresh failed after save.')
        })

      // Mark as saved immediately since API call succeeded (don't wait for refresh)
      resetAutoSaveFailures()
      setAutoSaveState({ isSaving: false, isEditing: false, isFailed: false })
    } finally {
      vm.suppressHeaderAutosave = false
      vm.headerSavingPanels = { vehicle: false, customer: false, insurer: false, booking: false }
      if (requestToken === vm.headerAutosaveToken) {
        vm.headerAutosaveInFlight = false
      }
    }
  }

  // Debounced header save — persists the latest computed totals once per editing burst.
  const debouncedSaveHeader = _debounce(async () => {
    if (!vm.autoSaveEnabled || isAnyEntityNew() || !vm.entity?.quoteId) return
    try {
      const quoteHeader = new QuoteLiteModel(vm.entity)
      await QuoteAutoSaveService.saveQuoteHeader(vm.entity.quoteId, quoteHeader)
    } catch (err) {
      console.error('Quote header auto save failed', err)
    }
  }, 800)

  // Debounced entity refresh — swaps in the server's latest state ~1s after the last save.
  const debouncedEntityRefresh = _debounce(() => {
    const itemTypes = quoteItemCollectionKeys
    vm.getLiteStoreItem(vm.$route.params.quoteId)
      .then(() => {
        // Guard the watchers while we mutate vm.entity so they don't push a spurious diff.
        vm._isAutosaveRefreshing = true

        if (vm.entity && vm.currentSnapshot?.modifiedDate) {
          vm.entity.modifiedDate = vm.currentSnapshot.modifiedDate
        }

        // Unsaved changes present: re-baseline to the live entity instead of swapping it in,
        // so cosmetic server-shape diffs (createdBy/dates/ordering) don't read as "unsaved".
        if (DeepDiff.diff(vm.baseSnapshot, vm.entity)) {
          // A save is pending — let it own the baseline; re-baselining now would mark its
          // unsaved changes (e.g. a drag-reorder) as already saved.
          if (hasPendingItemsSave()) {
            vm.saveSnapshot(_cloneDeep(vm.entity))
            vm.$nextTick(() => {
              vm._isAutosaveRefreshing = false
            })
            return
          }
          // Strip isNew items so they're still treated as "added" by the next save.
          const newBaseSnapshot = _cloneDeep(vm.entity)
          itemTypes.forEach((type) => {
            if (Array.isArray(newBaseSnapshot[type])) {
              newBaseSnapshot[type] = newBaseSnapshot[type].filter((item) => !item?.isNew)
            }
          })
          vm.setBaseSnapshot(newBaseSnapshot)
          vm.saveSnapshot(_cloneDeep(vm.entity))
          vm.$nextTick(() => {
            vm._isAutosaveRefreshing = false
          })
          return
        }

        // Capture empty items before swap so they are preserved in the UI.
        const emptyItemsByType = {}
        itemTypes.forEach((type) => {
          emptyItemsByType[type] = (vm.entity[type] || []).filter(
            (item) => item.isNew && (!item.itemDesc || item.itemDesc === '') && (!item.value || item.value === 0)
          )
        })

        vm.entity = _cloneDeep(vm.currentSnapshot)

        // Restore empty items (they were not sent to the server).
        itemTypes.forEach((type) => {
          const existingIds = new Set((vm.entity[type] || []).map((l) => l.quoteItemId))
          const emptyItemsToRestore = emptyItemsByType[type].filter((item) => !existingIds.has(item.quoteItemId))
          vm.entity[type] = [...(vm.entity[type] || []), ...emptyItemsToRestore]
        })

        vm.saveSnapshot(_cloneDeep(vm.entity))

        vm.$nextTick(() => {
          vm._isAutosaveRefreshing = false
        })
      })
      .catch(err => {
        console.error('Quote item refresh failed after save:', err)
        vm._isAutosaveRefreshing = false
      })
  }, 1000)

  // ── Bulk quote-items autosave ─────────────────────────────────────────────
  // Debounced, single-flight PUT of the full managed item set. "Synced" = !item.isNew:
  // a new item deleted before it syncs is dropped; a synced item that is deleted is sent
  // with deleted=true; empty (blank itemDesc) items are kept in the UI but never sent.
  const runItemsSave = async function () {
    if (!vm.autoSaveEnabled || isAnyEntityNew() || vm.entity?.readOnly) return
    if (vm._isAutosaveRefreshing) return

    // Single-flight: coalesce concurrent calls; remember that more work arrived.
    if (vm._itemsSaveInFlight) {
      vm._itemsSaveQueued = true
      return
    }

    // Defer while the user is mid-edit — a focused textbox/select or an open dropdown.
    // Saving now would trigger the post-save refresh, re-render the rows, and steal focus
    // (closing the dropdown / interrupting typing). Re-run once the field is blurred.
    if (isQuoteItemEditInProgress()) {
      setAutoSaveState({ isEditing: true, isSaving: false })
      vm._itemsSaveQueued = true
      startQuoteItemsEditDeferMonitor()
      return
    }

    vm._itemsSaveInFlight = true
    vm._itemsSavePending = false
    try {
      // Let any pending reactive update settle before snapshotting.
      await vm.$nextTick()

      // An emptied synced item is never saved — revert it to its last-saved state now
      // so the post-save baseline (and sessionStorage) never carries the blank description.
      const revertedEmptyItems = revertEmptySavedLineItems()

      // Nothing changed since the last saved baseline → no-op.
      if (!DeepDiff.diff(vm.baseSnapshot, vm.entity)) {
        if (revertedEmptyItems) vm.saveSnapshot(_cloneDeep(vm.entity))
        if (!hasPendingAutoSaveDiffs()) setAutoSaveState({ isEditing: false, isSaving: false })
        return
      }

      // De-dupe line numbers for new items (server max + sequential reassign).
      await vm.assignLineNumbers(false)

      // Build the full payload, and collect never-synced deleted items to drop locally.
      const payload = []
      const localRemovals = {}
      quoteItemCollectionKeys.forEach((type) => {
        const items = vm.entity[type] || []
        items.forEach((item) => {
          if (!item) return
          if (item.deleted) {
            if (!item.isNew) {
              payload.push(item) // synced delete → tell the server to remove it
            } else {
              // Added then deleted before it ever synced → omit + remove from the live array.
              (localRemovals[type] || (localRemovals[type] = [])).push(item.quoteItemId)
            }
          } else if (isValidItemAutoSave(item)) {
            payload.push(item) // add (isNew) or update; empty items stay in the UI, not sent
          }
        })
      })

      Object.keys(localRemovals).forEach((type) => {
        const ids = new Set(localRemovals[type])
        vm.entity[type] = (vm.entity[type] || []).filter((i) => !ids.has(i.quoteItemId))
      })

      if (payload.length === 0) {
        // Only empty / never-synced-deleted items changed — just advance the snapshot.
        vm.saveSnapshot(_cloneDeep(vm.entity))
        if (!hasPendingAutoSaveDiffs()) setAutoSaveState({ isEditing: false, isSaving: false })
        return
      }

      setAutoSaveState({ isSaving: true, isFailed: false })
      // Snapshot the state being sent before the await; the baseline is derived from this so
      // changes made during the in-flight save stay a diff and aren't lost.
      const entitySnapshotForSave = _cloneDeep(vm.entity)
      const saved = _cloneDeep(payload)
      await QuoteAutoSaveService.saveQuoteItems(vm.entity.quoteId, saved)

      // Mark the saved (non-deleted) items as synced on the live entity.
      const savedIds = new Set(payload.filter((i) => !i.deleted).map((i) => i.quoteItemId))
      quoteItemCollectionKeys.forEach((type) => {
        (vm.entity[type] || []).forEach((item) => {
          if (item.isNew && savedIds.has(item.quoteItemId)) item.isNew = false
        })
      })

      // Advance the baseline from the sent snapshot (isNew flattened, never-synced deletes dropped).
      const baseline = entitySnapshotForSave
      quoteItemCollectionKeys.forEach((type) => {
        if (Array.isArray(baseline[type])) {
          baseline[type] = baseline[type]
            .filter((i) => !(i.isNew && i.deleted))
            .map((i) => (i && savedIds.has(i.quoteItemId) ? { ...i, isNew: false } : i))
        }
      })
      StoreUtil.setStorage(baseline.quoteId, 'quote', baseline)
      vm.setBaseSnapshot(_cloneDeep(baseline))
      vm.saveSnapshot(_cloneDeep(vm.entity))

      resetAutoSaveFailures()
      setAutoSaveState({ isSaving: false, isEditing: false, isFailed: false })

      // Persist computed header totals + reconcile from the server (both already debounced).
      debouncedSaveHeader()
      debouncedEntityRefresh()
    } catch (error) {
      console.error('Quote items bulk save failed', error)
      setAutoSaveState({
        isSaving: false,
        isFailed: true,
        failMessage: error?.message || 'Automatic quote item save failed.'
      })
      registerAutoSaveFailure(error?.message)
    } finally {
      vm._itemsSaveInFlight = false
      if (vm._itemsSaveQueued) {
        vm._itemsSaveQueued = false
        runItemsSave()
      }
    }
  }

  const runItemsSaveDebounced = _debounce(runItemsSave, 800)

  // Public entry point: called by the item-array watchers on every change.
  const scheduleItemsSave = function () {
    if (!vm.autoSaveEnabled || isAnyEntityNew() || vm.entity?.readOnly) return
    if (vm._isAutosaveRefreshing) return
    if (vm.$route && vm.$route.name !== QuoteRoutes.QuoteBuilder.name) return
    vm._itemsSavePending = true
    setAutoSaveState({ isEditing: true })
    runItemsSaveDebounced()
  }
  scheduleItemsSave.cancel = () => {
    runItemsSaveDebounced.cancel()
    vm._itemsSavePending = false
  }
  scheduleItemsSave.flush = () => {
    runItemsSaveDebounced.flush()
  }

  // True while a bulk save is pending, in flight, or queued — used by the leave warning.
  const hasPendingItemsSave = () => {
    return !!(vm._itemsSavePending || vm._itemsSaveInFlight || vm._itemsSaveQueued)
  }

  // Re-fire the bulk save once the user is no longer mid-edit (field blurred / dropdown closed).
  const startQuoteItemsEditDeferMonitor = () => {
    if (vm.quoteItemsEditDeferMonitor) return
    vm.quoteItemsEditDeferMonitor = setInterval(() => {
      if (!vm.autoSaveEnabled || isAnyEntityNew() || vm.$route.name !== QuoteRoutes.QuoteBuilder.name) {
        stopQuoteItemsEditDeferMonitor()
        return
      }
      if (isQuoteItemEditInProgress()) return
      stopQuoteItemsEditDeferMonitor()
      vm._itemsSaveQueued = false
      vm.$nextTick(() => runItemsSave())
    }, 250)
  }

  const stopQuoteItemsEditDeferMonitor = () => {
    if (!vm.quoteItemsEditDeferMonitor) return
    clearInterval(vm.quoteItemsEditDeferMonitor)
    vm.quoteItemsEditDeferMonitor = null
  }

  const queueHeaderAutosave = (panelName = null) => {
    if (!vm.autoSaveEnabled) return
    if (!vm.entity || vm.readOnlyView || isAnyEntityNew()) return
    if (vm.headerAutosaveInFlight) return
    if (vm.isCustomerCreateModifyModalActive) return

    if (vm.validateError) {
      vm.$notification.openNotificationWithType('danger', 'AutoSave Paused', 'Autosaved has been paused due to validation errors.')
      vm.showItemsValidationErrors()
      return false
    }

    if (!hasPendingAutoSaveDiffs()) return

    // Only proceed with the header asset pipeline when there are asset changes.
    // Entity-only changes (e.g. claimNo on insurer panel) are handled below.
    const hasAssetDiffs = vm.snapshotDiffCustomer || vm.snapshotDiffVehicle || vm.snapshotDiffInsurer

    // If no asset diffs exist, nothing for the header pipeline to save.
    // Trigger an immediate entity save if entity fields changed (e.g. claimNo, policyNo on insurer panel).
    if (!hasAssetDiffs) {
      if (vm.snapshotDiff) {
        if (panelName) {
          vm.headerSavingPanels = { ...vm.headerSavingPanels, [panelName]: true }
        }
        autoSaveQuote()
      }
      return
    }
    setAutoSaveState({ isEditing: true })
    vm.headerAutosaveToken += 1
    vm.headerAutosaveInFlight = true
    autoUpdateQuoteHeader(vm.headerAutosaveToken)
  }

  const startQuoteAutosaveDropdownDeferredMonitor = (source = '') => {
    if (vm.quoteAutosaveDropdownDeferredMonitor) return

    vm.quoteAutosaveDropdownDeferredMonitor = setInterval(() => {
      if (!vm.autoSaveEnabled || isAnyEntityNew()) {
        stopQuoteAutosaveDropdownDeferredMonitor()
        return
      }

      // Wait until dropdowns close.
      if (isAnyQuoteItemDropdownOpen()) return

      stopQuoteAutosaveDropdownDeferredMonitor()
      // Re-queue the quote autosave.
      vm.$nextTick(() => autoSaveQuote(source ? `deferred:dropdown:${source}` : 'deferred:dropdown'))
    }, 250)
  }

  const stopQuoteAutosaveDropdownDeferredMonitor = () => {
    if (!vm.quoteAutosaveDropdownDeferredMonitor) return
    clearInterval(vm.quoteAutosaveDropdownDeferredMonitor)
    vm.quoteAutosaveDropdownDeferredMonitor = null
  }

  // Revert synced (isNew=false) items whose description was blanked back to their
  // last-saved state from baseSnapshot. A blank description is never autosaved, so
  // leaving it on the live entity would contaminate the baseline / hard-save payload.
  // Returns true when at least one item was reverted.
  const revertEmptySavedLineItems = () => {
    if (!vm.entity || !vm.baseSnapshot) return false
    let reverted = false
    quoteItemCollectionKeys.forEach((key) => {
      const items = vm.entity[key]
      if (!Array.isArray(items)) return
      items.forEach((item, idx) => {
        if (!item || item.isNew || item.deleted || !isBlankItemDesc(item.itemDesc)) return
        const baseItem = (vm.baseSnapshot[key] || []).find((b) => b && b.quoteItemId === item.quoteItemId)
        if (baseItem && !isBlankItemDesc(baseItem.itemDesc)) {
          items.splice(idx, 1, _cloneDeep(baseItem))
          reverted = true
        }
      })
    })
    return reverted
  }

  // Drop the entity.lines counter back to (highest line number still in use) + 1 so the
  // numbers freed by purged blank lines are reused by the next added item. Mirrors
  // nextLineNumber's collections/exclusions and assignLineNumbers' AudaNet guard and
  // subQuoteNo*1000 floor (supplementary quotes must not restart numbering below it).
  const resetLineNumberCounter = () => {
    if (vm.entity.isAudaNet || vm.entity.isAudaBridge) return
    const lineNumbers = [...quoteItemCollectionKeys, 'opgs'].flatMap((key) =>
      (vm.entity[key] || [])
        .filter((i) => i && !(i.isNew && i.deleted))
        .map((i) => i.lineNumber || 0)
    )
    const maxUsed = Math.max((vm.entity.subQuoteNo || 0) * 1000, ...lineNumbers, 0)
    vm.entity.lines = maxUsed + 1
  }

  const deleteEmptyLineItems = () => {
    if (!vm.entity) return
    // Emptied synced items go back to their last-saved state instead of being deleted.
    revertEmptySavedLineItems()
    let purgedAny = false
    quoteItemCollectionKeys.forEach((key) => {
      if (!vm.entity[key]) return
      const isPurgeable = (i) => isBlankItemDesc(i.itemDesc) && i.isNew && !i.deleted
      const purged = vm.entity[key].filter(isPurgeable)
      if (purged.length === 0) return
      purgedAny = true
      vm.entity[key] = vm.entity[key].filter((i) => !isPurgeable(i))
      // Sync each removal into AddItemsComponent's innerValue cache (same dance as the
      // trash-button delete), or its value-watcher preserve loop resurrects the purged
      // items and re-emits them to the parent while the add-items drawer is open.
      if (vm.$eventHub) {
        purged.forEach((item) => vm.$eventHub.$emit(EventHubTypes.QuoteItemDeletedSync, item))
      }
    })
    if (purgedAny) resetLineNumberCounter()
    // Update snapshot to clear diffs after deleting empty items
    vm.saveSnapshot(_cloneDeep(vm.entity))
  }

  const handleNavigationWarningConfirm = () => {
    // Delete empty items if that's the action
    if (vm.navigationWarningAction === 'deleteEmpty') {
      deleteEmptyLineItems()
      // Skip the next navigation warning and unsaved modal checks
      vm.skipNextNavWarning = true
      vm.skipNextUnsavedModal = true
      // Close the modal and navigate
      vm.isNavigationWarningModalActive = false
      vm.$nextTick(() => {
        vm.$router.push(vm.toRoute)
      })
    } else if (vm.navigationWarningAction === 'trySave') {
      // Close the modal and try to save
      vm.isNavigationWarningModalActive = false
      vm.save()
    } else if (vm.navigationWarningAction === 'leaveWithErrors') {
      // User chose to leave anyway despite save errors
      vm.leaveWithUnsavedErrors = true
      vm.skipNextNavWarning = true
      vm.skipNextUnsavedModal = true
      vm.isNavigationWarningModalActive = false
      vm.$nextTick(() => {
        vm.$router.push(vm.toRoute)
      })
    }
  }

  const handleNavigationWarningCancel = () => {
    if (vm.navigationWarningAction === 'trySave') {
      // User chose to skip anyway, navigate away despite autosave failure
      vm.skipNextNavWarning = true
      vm.skipNextUnsavedModal = true
      vm.isNavigationWarningModalActive = false
      vm.$nextTick(() => {
        vm.$router.push(vm.toRoute)
      })
    } else {
      // User chose to keep editing
      vm.isNavigationWarningModalActive = false
    }
  }

  const hasEmptyLineItems = () => {
    if (!vm.entity) return false
    return quoteItemCollectionKeys.some((key) => {
      const items = vm.entity[key]
      return items && items.some((i) => !i.deleted && isBlankItemDesc(i.itemDesc) && i.isNew)
    })
  }

  /**
   * Saves the quote first (if needed) and then executes the provided action.
   * When autosave is enabled and there are unsaved changes, it flushes all pending
   * autosave work and saves before continuing. When autosave is genuinely disabled,
   * it returns reason 'autosave-disabled' so the caller can warn the user to save manually.
   *
   * @param {Function} action - The async function to execute after saving
   * @returns {Promise<{ok: boolean, reason?: 'autosave-disabled'|'blocked'|'failed'}>}
   *   ok:true once the action has run; otherwise a reason describing why it could not.
   *   Only 'autosave-disabled' should surface the "please save" toast — 'blocked' (a
   *   modal/conflict is driving the UX) and 'failed' (the "Autosave Paused" notification
   *   already covers it) must stay silent so we never tell a user to save when there is
   *   no manual Save available.
   */
  const saveQuoteAndContinue = async (action) => {
    // If no unsaved changes, just execute the action
    if (!vm.snapshotDiff) {
      if (action && typeof action === 'function') {
        await action()
      }
      return { ok: true }
    }

    // Autosave genuinely off → caller shows the "Quote has changed. Please save" toast.
    if (!vm.autoSaveEnabled) {
      return { ok: false, reason: 'autosave-disabled' }
    }

    // Autosave is on but the quote can't be saved right now (view-only, or a header
    // asset is still new/unsaved). Stay silent — there is no manual Save to point at.
    // NOTE: gate on readOnlyView (the true view-only state), not entity.readOnly, so a
    // locked-but-editable quote still autosaves-and-continues (matches FQ-2963).
    if (vm.readOnlyView || isAnyEntityNew()) {
      return { ok: false, reason: 'blocked' }
    }

    // Autosave is enabled - flush pending work, save, then continue.
    try {
      setAutoSaveState({ isSaving: true, isFailed: false })

      // Flush the debounced item save so it can't race the save below. autoSave()'s
      // putEntity persists the full entity (including the latest item edits) and then
      // reconciles assets via getEntity, so cancelling the debounce loses no work.
      scheduleItemsSave.cancel()

      const result = await autoSave()

      if (result?.status === 'saved' || result?.status === 'no-diff') {
        resetAutoSaveFailures()
        setAutoSaveState({
          isSaving: false,
          isEditing: false,
          isFailed: false
        })

        // Execute the action after successful save
        if (action && typeof action === 'function') {
          await action()
        }
        return { ok: true }
      } else if (result?.status === 'blocked') {
        // The new-owner / customer create-modify modal is driving the UX.
        setAutoSaveState({ isSaving: false, isEditing: true })
        return { ok: false, reason: 'blocked' }
      } else if (result?.status === 'conflict') {
        // Server-side concurrent edit — surface the conflict modal, not the save toast.
        setAutoSaveState({ isSaving: false, isEditing: true })
        vm.isSaveConflictModalActive = true
        return { ok: false, reason: 'blocked' }
      } else {
        // Save failed — the "Autosave Paused" notification covers this.
        setAutoSaveState({
          isSaving: false,
          isEditing: true,
          isFailed: true,
          failMessage: result?.failMessage || 'Automatic quote save failed.'
        })
        registerAutoSaveFailure(result?.failMessage)
        return { ok: false, reason: 'failed' }
      }
    } catch (error) {
      console.error('Save and continue failed', error)
      setAutoSaveState({
        isSaving: false,
        isFailed: true,
        failMessage: error?.message || 'Automatic quote save failed.'
      })
      return { ok: false, reason: 'failed' }
    }
  }

  const saveLiteHeaderNow = async () => {
    if (!vm.autoSaveEnabled || !vm.entity || vm.entity.isNew || vm.entity.readOnly) return
    try {
      setAutoSaveState({ isSaving: true, isFailed: false })
      const quoteHeader = new QuoteLiteModel(vm.entity)
      await QuoteAutoSaveService.saveQuoteHeader(vm.entity.quoteId, quoteHeader)
      // Sync modifiedDate from server to prevent a false-positive conflict
      await vm.getLiteStoreItem(vm.entity.quoteId)
      if (vm.currentSnapshot?.modifiedDate) {
        vm.entity.modifiedDate = vm.currentSnapshot.modifiedDate
      }
      resetAutoSaveFailures()
      setAutoSaveState({ isSaving: false, isEditing: false, isFailed: false })
      vm.saveSnapshot(_cloneDeep(vm.entity))
      vm.setBaseSnapshot(_cloneDeep(vm.entity))
    } catch (error) {
      console.error('Lite header save failed', error)
      setAutoSaveState({ isSaving: false, isFailed: true, failMessage: error?.message || 'Quote header save failed.' })
      registerAutoSaveFailure(error?.message)
    }
  }

  const saveJobStageNow = async () => {
    if (!vm.autoSaveEnabled || vm.autoSaveDisabled || !vm.entity || vm.entity.isNew) return
    try {
      setAutoSaveState({ isSaving: true, isFailed: false })
      await QuoteAutoSaveService.saveJobStage(vm.entity.quoteId, {
        jobStage: vm.entity.jobStage,
        subJobStage: vm.entity.subJobStage
      })
      // Sync modifiedDate to prevent a false-positive conflict on the next debounced save
      await vm.getLiteStoreItem(vm.entity.quoteId)
      if (vm.currentSnapshot?.modifiedDate) {
        vm.entity.modifiedDate = vm.currentSnapshot.modifiedDate
      }
      resetAutoSaveFailures()
      setAutoSaveState({ isSaving: false, isEditing: false, isFailed: false })
      vm.saveSnapshot(_cloneDeep(vm.entity))
      vm.setBaseSnapshot(_cloneDeep(vm.entity))
    } catch (error) {
      console.error('Job stage autosave failed', error)
      setAutoSaveState({ isSaving: false, isFailed: true, failMessage: error?.message || 'Job stage save failed.' })
      registerAutoSaveFailure(error?.message)
    }
  }

  const saveReadOnlyNow = async () => {
    // Note: do NOT guard on entity.readOnly — we are explicitly saving a change to it.
    if (!vm.autoSaveEnabled || vm.autoSaveDisabled || !vm.entity || vm.entity.isNew) return
    try {
      setAutoSaveState({ isSaving: true, isFailed: false })
      await QuoteAutoSaveService.saveReadOnly(vm.entity.quoteId, {
        readOnly: vm.entity.readOnly
      })
      // Sync modifiedDate to prevent a false-positive conflict on the next debounced save
      await vm.getLiteStoreItem(vm.entity.quoteId)
      if (vm.currentSnapshot?.modifiedDate) {
        vm.entity.modifiedDate = vm.currentSnapshot.modifiedDate
      }
      resetAutoSaveFailures()
      setAutoSaveState({ isSaving: false, isEditing: false, isFailed: false })
      vm.saveSnapshot(_cloneDeep(vm.entity))
      vm.setBaseSnapshot(_cloneDeep(vm.entity))
    } catch (error) {
      console.error('Read-only autosave failed', error)
      setAutoSaveState({ isSaving: false, isFailed: true, failMessage: error?.message || 'Read-only save failed.' })
      registerAutoSaveFailure(error?.message)
    }
  }

  // Exposed methods
  return {
    autoSave, // Exposed specifically for manual triggers if any
    autoSaveQuote,
    autoUpdateQuoteHeader,
    scheduleItemsSave,
    runItemsSave,
    hasPendingItemsSave,
    stopQuoteItemsEditDeferMonitor,
    queueHeaderAutosave,
    isValidItemAutoSave,
    hasPendingAutoSaveWork,
    hasPendingAutoSaveDiffs,
    registerAutoSaveFailure,
    resetAutoSaveFailures,
    setAutoSaveState,
    deleteEmptyLineItems,
    revertEmptySavedLineItems,
    hasEmptyLineItems,
    handleNavigationWarningConfirm,
    handleNavigationWarningCancel,
    saveQuoteAfterHeaderChangeNow,
    hasHeaderQuoteIdChanges,
    getExpandedQuoteItemId,
    getFocusedItemInfo,
    findItemIdByLineAndType,
    saveQuoteAndContinue,
    saveLiteHeaderNow,
    saveJobStageNow,
    saveReadOnlyNow,
    debouncedSaveHeader,
    debouncedEntityRefresh
  }
}
